import find from 'lodash/find';
import segment from '../core/segment';

const colorsMap = {
  blues: 'dark-blue',
  country: 'green',
  folk: 'gold',
  funk: 'light-teal',
  pop: 'purple',
  rock: 'red',
};

const instrumentsMap = {
  'acoustic-guitar': {
    instrument: 'acoustic-guitar',
    image: 'https://stuff.fendergarage.com/images/j/y/G/onboarding-instrument-acoustic-guitar.jpg',
    title: 'Acoustic Guitar',
    paths: null,
  },
  'electric-guitar': {
    instrument: 'electric-guitar',
    image:
      'https://stuff.fendergarage.com/images/i/O/C/onboarding-instrument-electric-guitar-telecaster.jpg',
    title: 'Electric Guitar',
    paths: null,
  },
  ukulele: {
    instrument: 'ukulele',
    image: 'https://stuff.fendergarage.com/images/D/Z/n/onboarding-instrument-ukulele.jpg',
    title: 'Ukulele',
    paths: null,
  },
  bass: {
    instrument: 'bass',
    image: 'https://stuff.fendergarage.com/images/W/x/k/onboarding-instrument-bass.jpg',
    title: 'Bass',
    paths: null,
  },
};

/**
 * Returns an object containting instruments with mapped paths associated
 * with each instrument.
 *
 * @param {array} pathList - list of paths
 */
function getInstruments(pathList) {
  if (pathList && Array.isArray(pathList)) {
    Object.keys(instrumentsMap).forEach(key => {
      instrumentsMap[key].paths = pathList.filter(
        path => path.instrument === instrumentsMap[key].instrument,
      );
    });
  }
  return instrumentsMap;
}

/**
 * Returns the instrument type.
 *
 * @param {string} str - instrument
 */
function getInstrumentType(instrument) {
  if (instrument && typeof instrument === 'string') {
    const type = instrument.split('-');
    return type.length > 1 ? type[0] : null;
  }
  return null;
}

/**
 * Returns the currently selected instrument.
 *
 * @param {object} selectedInstrument - current instrument object
 */
function getPaths(selectedInstrument) {
  if (selectedInstrument && typeof selectedInstrument === 'string') {
    return find(instrumentsMap, { instrument: selectedInstrument }) || null;
  }
  return null;
}

/**
 * Returns a boolean for the disabled state of the set path button.
 *
 * @param {string} - current path id
 * @param {string} - defaulted path id, typically if a instrument has only one path (ukulele)
 */
function isDisabled(currentPathID, defaultedPathID) {
  if (currentPathID || defaultedPathID) {
    return false;
  }
  return true;
}

/**
 * Takes an SHA256 array buffer and converts it to a hex encoded string
 *
 * @param {ArrayBuffer} buffer - An ArrayBuffer usually resulting from a digest function
 * @returns {string} - Buffer converted to string
 */
function sha256BufferToString(buffer) {
  const byteArray = new Uint8Array(buffer);
  const hexCodes = [...byteArray].map(value => {
    const hexCode = value.toString(16);
    const paddedHexCode = hexCode.padStart(2, '0');
    return paddedHexCode;
  });
  return hexCodes.join('');
}

/**
 * Creates a hash of a given string (SHA-256)
 *
 * @param {string} input - Input to hash
 * @returns {Promise<ArrayBuffer>} - An SHA-256 hash
 */
async function hashSHA256(input) {
  const msgBuffer = new TextEncoder('utf-8').encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  return hashBuffer;
}

/**
 * Loads a tracking script used on the quiz route.
 * Third party Bounce script comes from:
 * https://fenderdigital.atlassian.net/browse/PT-2081
 *
 * @param {Object} trackingFields - Fields to be tracked by the bounceX pixel
 * @param {string} trackingFields.email - The email to be used as a hash for tracking purposes
 * @param {string} trackingFields.orderId - Field that maps to order_id, order identifier
 * @param {number} trackingFields.amount - The amount to be used
 * @param {string} trackingFields.goal - Campaign goal, usually subscription_purchase
 * @param {(input: string) => Promise<ArrayBuffer>} hashFunction - A function used to hash the email
 * @param {(buffer: ArrayBuffer) => String} bufferConverter - Convert the hash to a string
 */
async function loadScript({ email, orderId, amount, goal }, hashFunction, bufferConverter) {
  const bounceWebsiteId = 3447;
  const emailSha256Buffer = await hashFunction(email.trim().toLowerCase());
  const emailSha256 = bufferConverter(emailSha256Buffer);
  window.top.bouncex = window.top.bouncex || [];
  window.top.bouncex.push([
    'conversion',
    {
      email_sha256: emailSha256,
      order_id: orderId,
      amount,
      goal,
    },
  ]);
  const pixel = document.createElement('img');
  pixel.height = 1;
  pixel.width = 1;
  pixel.border = 0;
  pixel.style = 'display:none;';
  pixel.id = 'bounce-pixel';
  pixel.src =
    `//api.bounceexchange.com/capture/convert2.gif?` +
    `website_id=${bounceWebsiteId}` +
    `&email_sha256=${emailSha256}` +
    `&order_id=${orderId}` +
    `&amount=${amount}` +
    `&goal=${goal}`;
  document.body.appendChild(pixel);
}

/**
 * Segment tracking event for setting initial path.
 *
 * @param {object} obj - tracking object.
 */
function trackEvent(obj) {
  const { instrument, type, style, userID } = obj;
  segment.event('Initial Path', {
    instrument,
    instrumentType: type,
    style,
    fcId: userID,
    siteId: 'play',
  });
}

export {
  colorsMap,
  getInstruments,
  getInstrumentType,
  getPaths,
  isDisabled,
  loadScript,
  trackEvent,
  sha256BufferToString,
  hashSHA256,
};
